import { createContext, useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useStaticQuery, graphql } from "gatsby";

import { GeoContext } from "./GeoProvider";
import { CartContext } from "./CartProvider";
import { getDistanceFromLatLonInKm, kmToMiles } from "@helpers/distance";
import { useALError } from "@helpers/ErrorBoundary/ALErrorBoundary";

// Only stores within this many miles of the customer are shown when we have a location.
const RADIUS_MILES = 50;

// FOR TESTING, override my location (CLUJ) with the New York location.
// const MOCK_COORDS = {
//   latitude: 40.7351,
//   longitude: -74.0039,
// };

const defaultStoreLocationContext = {
  storesAvailable: [], // stores shown in the popup
  currentStore: null, // store shown on the PDP (picked one, or nearest)
  selectStore: () => {}, // commit the store the customer confirmed in the popup
  isOpen: false,
  openPopup: () => {},
  closePopup: () => {},
  hasLocation: false,
  isSearching: false,
  resolveSearchLocation: () => {},
  clearSearchLocation: () => {},
  requestUserLocation: () => {},
};

export const StoreLocationContext = createContext(defaultStoreLocationContext);

export function getNearbyStores(stores, origin) {
  return (stores || [])
    .filter((store) => store.latitude && store.longitude)
    .map((store) => ({
      ...store,
      distanceMiles: kmToMiles(
        getDistanceFromLatLonInKm(
          origin.latitude,
          origin.longitude,
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        )
      ),
    }))
    .filter((store) => store.distanceMiles <= RADIUS_MILES)
    .sort((a, b) => a.distanceMiles - b.distanceMiles);
}

export default function StoreLocationProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchCoords, setSearchCoords] = useState(null);

  const { sendReport } = useALError();
  const { coords, initUserLocation, gePriceDetails, clientIp } = useContext(GeoContext);
  const { cart } = useContext(CartContext);
  const countryCode = gePriceDetails?.CountryCode;

  const hasLocation = !!(coords?.latitude && coords?.longitude);
  const isSearching = !!(searchCoords?.latitude && searchCoords?.longitude);

  // Fetch the retail stores
  const data = useStaticQuery(graphql`
    query StoreLocationStores {
      retailStoresData: allContentfulRetailLp(filter: { node_locale: { eq: "en-US" } }) {
        edges {
          node {
            ...RetailLpFields
          }
        }
      }
    }
  `);

  const stores = useMemo(
    () =>
      (data?.retailStoresData?.edges?.[0]?.node?.stores || []).filter(
        (store) => store.bopisEnabled
      ),
    [data]
  );

  // With a location / search: stores within 50 miles, closest first.
  // Without a location: all BOPIS stores so the customer can still choose one.
  const nearbyFromUser = useMemo(
    () => (hasLocation ? getNearbyStores(stores, coords) : []),
    [stores, hasLocation, coords]
  );
  const nearestStore = nearbyFromUser[0] || null;
  const currentStore = selectedStore || nearestStore;

  const nearbyFromSearch = useMemo(
    () => (isSearching ? getNearbyStores(stores, searchCoords) : []),
    [stores, isSearching, searchCoords]
  );

  const storesAvailable = useMemo(() => {
    if (isSearching) {
      return nearbyFromSearch;
    }
    if (hasLocation) {
      return nearbyFromUser;
    }
    return stores || [];
  }, [isSearching, nearbyFromSearch, hasLocation, nearbyFromUser, stores]);

  const openPopup = useCallback(() => setIsOpen(true), []);
  const closePopup = useCallback(() => {
    setIsOpen(false);
    setSearchCoords(null);
  }, []);

  // Commit the store the customer confirmed in the popup
  const selectStore = useCallback(
    async (store, pickupHandle) => {
      setSelectedStore(store);

      if (!pickupHandle || !cart?.cartId || !countryCode) {
        return;
      }

      try {
        await axios.post(
          "/.netlify/functions/update-cart-buyer-identity",
          {
            cartId: cart.cartId,
            countryCode,
            pickupHandle,
          },
          { headers: { ip_address: clientIp } }
        );
      } catch (error) {
        sendReport(error, { name: "StoreLocationProvider", priority: "P1" });
      }
    },
    [cart?.cartId, countryCode, clientIp, sendReport]
  );

  const resolveSearchLocation = useCallback(({ lat, lng }) => {
    if (lat == null || lng == null) {
      setSearchCoords(null);
      return;
    }
    setSearchCoords({ latitude: lat, longitude: lng });
  }, []);

  const clearSearchLocation = useCallback(() => {
    setSearchCoords(null);
  }, []);

  const requestUserLocation = useCallback(() => {
    setSearchCoords(null);
    initUserLocation();
  }, [initUserLocation]);

  const value = useMemo(
    () => ({
      storesAvailable,
      currentStore,
      selectStore,
      isOpen,
      openPopup,
      closePopup,
      hasLocation,
      isSearching,
      resolveSearchLocation,
      clearSearchLocation,
      requestUserLocation,
    }),
    [
      storesAvailable,
      currentStore,
      selectStore,
      isOpen,
      openPopup,
      closePopup,
      hasLocation,
      isSearching,
      resolveSearchLocation,
      clearSearchLocation,
      requestUserLocation,
    ]
  );

  return <StoreLocationContext.Provider value={value}>{children}</StoreLocationContext.Provider>;
}

StoreLocationProvider.propTypes = {
  children: PropTypes.node,
};
